import { TimelineLite } from "gsap";
import * as HLF from "html-living-framework";
import { ResultType } from "../States/Results";

enum SpineTrack {
    BODY,
    EYES,
    MOUTH,
    NUM_ELEMENTS,
}

interface IAnimData {
    key: string;
    looped?: boolean;
    track?: SpineTrack;
    reset?: boolean;
}

interface IReactionData {
    anims: IAnimData[];
    vo: string[];
}

export class Fabuloso extends PIXI.Container {
    private static readonly ANIM_ADJUST: IAnimData = {
        key: "adjust",
    };
    private static readonly ANIM_AMAZED: IAnimData = {
        key: "amazed",
    };
    private static readonly ANIM_BLINK: IAnimData = {
        key: "blink",
        track: SpineTrack.EYES,
    };
    private static readonly ANIM_FAIL: IAnimData = {
        key: "fail",
        looped: true,
    };
    private static readonly ANIM_GREETING: IAnimData = {
        key: "greeting",
    };
    private static readonly ANIM_IDLE: IAnimData = {
        key: "idle",
        looped: true,
        reset: true,
    };
    private static readonly ANIM_PRESENT: IAnimData = {
        key: "present",
    };
    private static readonly ANIM_THINKING: IAnimData = {
        key: "thinking",
    };
    private static readonly ANIM_WIN: IAnimData = {
        key: "win",
        looped: true,
    };
    private static readonly ANIM_WIPEHAT: IAnimData = {
        key: "wipehat",
    };

    private static readonly REACTION_SUCCESS: IReactionData = {
        anims: [ Fabuloso.ANIM_AMAZED, Fabuloso.ANIM_WIN ],
        vo: [],
    };
    private static readonly REACTION_FAILURE: IReactionData = {
        anims: [ Fabuloso.ANIM_FAIL ],
        vo: [],
    };
    private static readonly REACTION_COMPLETE: IReactionData = {
        anims: [ Fabuloso.ANIM_WIN ],
        vo: [],
    };

    private _spine: PIXI.spine.Spine;
    private _blinkTimer!: TimelineLite;

    constructor() {
        super();

        this._spine = HLF.PixiUtils.makeSpine("fabuloso");
        this._spine.scale.set(0.5);
        this.addChild(this._spine);

        this._spine.state.addListener({
            complete: this.onAnimComplete.bind(this),
        });

        this.playAnim(Fabuloso.ANIM_IDLE);
        this.queueBlink();
    }

    public update(pDeltaTime: number): void {
        this._spine.update(pDeltaTime);
    }

    public destroy(pOptions?: boolean | PIXI.DestroyOptions): void {
        if (this._blinkTimer !== undefined) {
            this._blinkTimer.kill();
        }
        this._spine.state.listeners = [];
        super.destroy(pOptions);
    }

    public playResultsAnim(pResult: ResultType): void {
        let reaction: IReactionData;
        switch (pResult) {
            case ResultType.SUCCESS:
                reaction = Fabuloso.REACTION_SUCCESS;
                break;
            case ResultType.COMPLETE:
                reaction = Fabuloso.REACTION_COMPLETE;
                break;
            case ResultType.FAIL:
            default:
                reaction = Fabuloso.REACTION_FAILURE;
                break;
        }
        this.playAnim(HLF.ArrayUtils.random(reaction.anims));
    }

    public playSelectionAnim(): void {
        this.playAnim(Fabuloso.ANIM_PRESENT);
    }

    private playAnim(pData: IAnimData): number {
        if (this._blinkTimer !== undefined) {
            this._blinkTimer.kill();
        }
        if (pData.reset) {
            this._spine.skeleton.setToSetupPose();
            this._spine.state.tracks = [];
        }
        const track: SpineTrack = pData.track ? pData.track : SpineTrack.BODY;
        const looped: boolean = (pData.looped !== undefined) ? pData.looped : false;
        this._spine.state.setAnimation(track, pData.key, looped);
        this._spine.update(0);
        return this._spine.state.getCurrent(track).animationEnd;
    }

    private queueBlink(): void {
        this._blinkTimer = new TimelineLite();
        this._blinkTimer.call(this.blink, undefined, this, HLF.Random.floatBetween(3, 8));
    }

    private blink(): void {
        this.playAnim(Fabuloso.ANIM_BLINK);
    }

    private onAnimComplete(pTrack: PIXI.spine.core.TrackEntry): void {
        switch (pTrack.animation.name) {
            default:
                if (pTrack.loop === false) {
                    this.playAnim(Fabuloso.ANIM_IDLE);
                    this.queueBlink();
                }
                break;
        }
    }
}
